import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Search, Menu, X, User, Heart, Moon, Sun, Sprout, Store, ShoppingBasket, ChefHat, Gift, GitCompareArrows, Utensils, BookOpen, ChevronLeft, Globe } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import SearchOverlay from "@/components/SearchOverlay";

const NAV = [
  { to: "/store?category=olive_oil", key: "nav_oliveoil" },
  { to: "/store", key: "nav_store" },
  { to: "/basket-builder", key: "nav_basket" },
  { to: "/cook", key: "nav_cook" },
  { to: "/box", key: "nav_box" },
  { to: "/heritage", key: "nav_heritage" },
];

const MENU_SECTIONS = [
  { titleKey: "section_shop", items: [
    { to: "/store?category=olive_oil", key: "nav_oliveoil", descKey: "d_oliveoil", icon: Sprout },
    { to: "/store", key: "nav_store", descKey: "d_store", icon: Store },
    { to: "/basket-builder", key: "nav_basket", descKey: "d_basket", icon: ShoppingBasket },
  ]},
  { titleKey: "section_discover", items: [
    { to: "/cook", key: "nav_cook", descKey: "d_cook", icon: ChefHat },
    { to: "/nutrition", key: "nav_nutrition", descKey: "d_nutrition", icon: Utensils },
    { to: "/box", key: "nav_box", descKey: "d_box", icon: Gift },
    { to: "/compare", key: "nav_compare", descKey: "d_compare", icon: GitCompareArrows },
    { to: "/heritage", key: "nav_heritage", descKey: "d_heritage", icon: BookOpen },
  ]},
];

export default function Navbar() {
  const { count } = useCart();
  const { user } = useAuth();
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("suhool_theme") === "dark");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("suhool_theme", dark ? "dark" : "light");
  }, [dark]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || open;

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { setOpen(false); navigate(`/store?q=${encodeURIComponent(q.trim())}`); }
  };

  const iconBtn = `flex h-10 w-10 items-center justify-center rounded-full transition-colors sm:h-11 sm:w-11`;
  const iconTone = solid ? "bg-olive/5 text-olive-dark hover:bg-olive/10" : "bg-white/15 text-ivory backdrop-blur hover:bg-white/25";

  return (
    <>
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        solid ? "glass border-b border-olive/10 shadow-sm" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:gap-4 sm:px-6 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex min-w-0 items-center gap-2.5">
          <img src="/logo.png" alt="شعار سهول جنين" className="h-10 w-10 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-olive/10 sm:h-11 sm:w-11 sm:rounded-2xl" />
          <span className="flex min-w-0 flex-col leading-none">
            <span className={`whitespace-nowrap font-display text-base font-bold transition-colors sm:text-lg ${solid ? "text-olive-dark" : "text-ivory"}`}>{t("brand")}</span>
            <span className={`text-[10px] tracking-wide transition-colors max-sm:hidden ${solid ? "text-olive" : "text-ivory/80"}`}>{t("tagline")}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <Link key={n.key} to={n.to}
              className={`link-underline text-sm font-medium transition-colors ${solid ? "text-ink/75 hover:text-olive" : "text-ivory/90 hover:text-white"}`}>
              {t(n.key)}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button onClick={() => setSearchOpen(true)} data-testid="search-open" aria-label={t("search_ph")}
            className={`${iconBtn} ${iconTone} sm:w-auto sm:px-3.5`}>
            <Search className="h-4 w-4" />
            <span className="hidden text-sm md:inline">{t("search_ph")}</span>
          </button>

          <button onClick={toggle} data-testid="lang-toggle"
            className={`hidden h-11 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors sm:flex ${iconTone}`}>
            <Globe className="h-4 w-4" /> {lang === "ar" ? "EN" : "ع"}
          </button>

          <button onClick={() => setDark((v) => !v)} data-testid="theme-toggle" aria-label={dark ? "الوضع النهاري" : "الوضع الليلي"}
            className={`hidden ${iconBtn} ${iconTone} sm:flex`}>
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <Link
            to="/account" data-testid="favorites-button" aria-label="المفضلة"
            className={`hidden h-11 w-11 items-center justify-center rounded-full transition-colors sm:flex ${iconTone}`}
          >
            <Heart className="h-5 w-5" />
          </Link>

          <Link
            to="/cart" data-testid="cart-button"
            className={`relative ${iconBtn} ${iconTone}`}
          >
            <ShoppingBag className="h-5 w-5" />
            <AnimatePresence mode="popLayout">
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.3, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  data-testid="cart-count"
                  className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-palred px-1 text-[11px] font-bold text-white"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>

          <Link
            to={user ? "/account" : "/login"} data-testid="account-button"
            className={`hidden h-11 w-11 items-center justify-center rounded-full transition-colors sm:flex ${iconTone}`}
          >
            <User className="h-5 w-5" />
          </Link>

          <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="القائمة"
            className={`${iconBtn} lg:hidden ${solid ? "text-olive-dark" : "text-ivory"}`}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
    </header>

    <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile slide-in drawer */}
      <div className={`fixed inset-0 z-[60] lg:hidden ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-olive-deep/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        />
        <aside
          data-testid="mobile-drawer"
          className={`absolute inset-y-0 right-0 flex w-[86%] max-w-sm flex-col bg-ivory shadow-2xl transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between border-b border-olive/10 px-5 py-4">
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="شعار سهول جنين" className="h-10 w-10 rounded-xl object-cover ring-1 ring-olive/10" />
              <div className="leading-none">
                <div className="font-display text-lg font-bold text-olive-dark">{t("brand")}</div>
                <div className="text-[10px] text-olive">{t("tagline")}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="drawer-close" className="flex h-10 w-10 items-center justify-center rounded-full bg-olive/5 text-ink/60">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5">
            <button onClick={() => { setOpen(false); setSearchOpen(true); }} data-testid="drawer-search-open"
              className="mb-6 flex w-full items-center gap-2 rounded-full bg-olive/5 px-4 py-3 text-sm text-ink/50">
              <Search className="h-4 w-4 text-olive" /> {t("search_ph")}
            </button>

            {MENU_SECTIONS.map((section, si) => (
              <div key={section.titleKey} className="mb-6" style={{ transitionDelay: `${si * 60}ms` }}>
                <div className="mb-2 px-1 text-xs font-semibold tracking-wide text-olive/70">{t(section.titleKey)}</div>
                <div className="space-y-1.5">
                  {section.items.map((it, i) => (
                    <Link
                      key={it.key} to={it.to}
                      className={`group flex items-center gap-3 rounded-2xl border border-olive/10 bg-white p-3 transition-all duration-300 hover:border-olive/30 hover:bg-olive/[0.03] ${open ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0"}`}
                      style={{ transitionDelay: open ? `${si * 120 + i * 55 + 120}ms` : "0ms" }}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-olive/8 text-olive transition-colors group-hover:bg-olive group-hover:text-ivory">
                        <it.icon className="h-5 w-5" />
                      </span>
                      <span className="flex-1">
                        <span className="block font-display text-sm font-semibold text-ink">{t(it.key)}</span>
                        <span className="block text-xs text-ink/50">{t(it.descKey)}</span>
                      </span>
                      <ChevronLeft className="h-4 w-4 text-ink/30 transition-transform group-hover:-translate-x-0.5 group-hover:text-olive" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-olive/10 p-5">
            <Link to={user ? "/account" : "/login"} className="flex items-center justify-center gap-2 rounded-full bg-olive py-3.5 font-semibold text-ivory">
              <User className="h-5 w-5" /> {user ? t("my_account") : t("login")}
            </Link>
            <div className="mt-3 flex gap-2">
              <button onClick={toggle} data-testid="drawer-lang-toggle"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-olive/20 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
                <Globe className="h-4 w-4" /> {lang === "ar" ? "English" : "عربي"}
              </button>
              <button onClick={() => setDark((v) => !v)} data-testid="drawer-theme-toggle"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-olive/20 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {dark ? "نهاري" : "ليلي"}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
